import React, { useState, useEffect } from 'react';
import API_BASE from '../config';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import SequenceConfigPanel from '../components/SequenceConfigPanel';

const SIGNAL_LABELS = { funding: 'Funding Round', hiring: 'Hiring Activity', leadership_change: 'Leadership Change', news: 'Company News', tech_change: 'Technology Change' };
const SIGNAL_COLOURS = {
  funding: { bg: '#F0FFF4', text: '#276749', border: '#276749' },
  hiring: { bg: '#EBF8FF', text: '#2B6CB0', border: '#2B6CB0' },
  leadership_change: { bg: '#FFF5F5', text: '#8B1A1A', border: '#8B1A1A' },
  news: { bg: '#FFFFF0', text: '#B7791F', border: '#B7791F' },
  tech_change: { bg: '#FAF5FF', text: '#553C9A', border: '#553C9A' },
};

function fmt(ts) {
  const d = new Date(ts), now = new Date(), diff = now - d;
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return Math.floor(diff / 60000) + 'm ago';
  if (diff < 86400000) return Math.floor(diff / 3600000) + 'h ago';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function SectionLabel({ children, count }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
      <span style={{ fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: '500' }}>{children}</span>
      {count !== undefined && <span style={{ fontSize: '0.68rem', color: '#8B1A1A', backgroundColor: '#F5E6E6', padding: '0.1rem 0.45rem', borderRadius: '20px', fontWeight: '600' }}>{count}</span>}
    </div>
  );
}

function ClientDashboard() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [icpExists, setIcpExists] = useState(true);

  // Get client_id from Clerk metadata
  const clientId = user?.publicMetadata?.client_id;
  const clientName = user?.publicMetadata?.client_name || 'Your workspace';

  useEffect(() => {
    if (!clientId) return;
    fetch(`${API_BASE}/clients/${clientId}/icp`)
      .then(r => r.json())
      .then(d => setIcpExists(!!(d && d.id)))
      .catch(() => setIcpExists(false));

    fetch(`${API_BASE}/dashboard-v2?client_id=${clientId}`)
      .then(r => r.json())
      .then(d => {
        // Filter to only this client's data
        setData({
          ...d,
          today_signals: (d.today_signals || []).filter(s => s.client_id === parseInt(clientId)),
          priority_contacts: (d.priority_contacts || []).filter(c => c.client_id === parseInt(clientId)),
          pending_sequences: (d.pending_sequences || []).filter(s => s.client_id === parseInt(clientId)),
          recent_activity: (d.recent_activity || []).filter(a => a.client_id === parseInt(clientId)),
        });
      })
      .catch(() => setError('Could not load dashboard.'))
      .finally(() => setLoading(false));
  }, [clientId]);

  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div>

      <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto', boxSizing: 'border-box' }}>

        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '1.4rem', fontWeight: '500', margin: '0 0 0.25rem', color: '#1A1A1A' }}>Good morning.</h1>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#888' }}>{today} — here is what's happening with your prospects today.</p>
        </div>

        {error && <p style={{ color: '#C53030', fontSize: '0.85rem' }}>{error}</p>}

        {!clientId && (
          <div style={{ background: '#FFF5F5', border: '0.5px solid #FEB2B2', borderRadius: '10px', padding: '1.5rem', textAlign: 'center' }}>
            <p style={{ margin: 0, color: '#C53030', fontSize: '0.85rem' }}>Your account is not linked to a workspace. Contact your administrator.</p>
          </div>
        )}

        {loading && clientId && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {[1,2,3].map(i => <div key={i} style={{ height: '80px', background: '#EEECEA', borderRadius: '10px' }} />)}
          </div>
        )}

        {!loading && data && (
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '2rem', alignItems: 'start' }}>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>

              {/* ICP setup prompt */}
              {!icpExists && (
                <div style={{ backgroundColor: '#FFF5F5', border: '0.5px solid #FEB2B2', borderLeft: '4px solid #8B1A1A', borderRadius: '10px', padding: '1.25rem' }}>
                  <p style={{ margin: '0 0 0.35rem', fontSize: '0.875rem', fontWeight: '600', color: '#8B1A1A' }}>Set up your ICP to unlock the platform</p>
                  <p style={{ margin: '0 0 0.75rem', fontSize: '0.82rem', color: '#4A5568', lineHeight: '1.5' }}>
                    Your Ideal Customer Profile (ICP) is the foundation of everything — signal detection, company search, contact discovery, and sequence generation all use your ICP to find and prioritise the right prospects.
                  </p>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button onClick={() => navigate(`/clients/${clientId}/discovery`)}
                      style={{ fontSize: '0.82rem', color: '#4A5568', background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer' }}>
                      Start with Discovery Brief
                    </button>
                    <button onClick={() => navigate(`/clients/${clientId}/icp`)}
                      style={{ fontSize: '0.82rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer', fontWeight: '500' }}>
                      Build my ICP →
                    </button>
                  </div>
                </div>
              )}

              {/* Signals */}
              <div>
                <SectionLabel count={data.today_signals?.length || 0}>Today's signals — your prospects</SectionLabel>
                {!data.today_signals?.length ? (
                  <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '2rem', textAlign: 'center' }}>
                    <p style={{ margin: 0, color: '#AAA', fontSize: '0.85rem' }}>No new signals in the last 48 hours. Signals are detected daily at 06:00.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {data.today_signals.map(signal => {
                      const c = SIGNAL_COLOURS[signal.signal_type] || { bg: '#F7F7F7', text: '#888', border: '#888' };
                      return (
                        <div key={signal.id} style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderLeft: `3px solid ${c.border}`, borderRadius: '10px', padding: '1rem 1.25rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.68rem', fontWeight: '600', color: c.text, backgroundColor: c.bg, padding: '0.15rem 0.5rem', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              {SIGNAL_LABELS[signal.signal_type] || signal.signal_type}
                            </span>
                            <span style={{ fontSize: '0.7rem', fontWeight: '600', color: c.text }}>Score {signal.relevance_score}/10</span>
                          </div>
                          <p style={{ margin: '0 0 0.25rem', fontSize: '0.9rem', fontWeight: '600', color: '#8B1A1A' }}>{signal.company_name}</p>
                          <p style={{ margin: '0 0 0.25rem', fontSize: '0.85rem', color: '#1A1A1A', lineHeight: '1.5' }}>{signal.signal_data?.summary}</p>
                          <p style={{ margin: 0, fontSize: '0.72rem', color: '#AAA' }}>{signal.contact_count} contact{signal.contact_count !== 1 ? 's' : ''} · {fmt(signal.created_at)}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Priority contacts */}
              <div>
                <SectionLabel count={data.priority_contacts?.length || 0}>Priority contacts — ready to engage</SectionLabel>
                {!data.priority_contacts?.length ? (
                  <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '2rem', textAlign: 'center' }}>
                    <p style={{ margin: 0, color: '#AAA', fontSize: '0.85rem' }}>No high-scoring contacts yet. Add prospects and run enrichment to populate this.</p>
                  </div>
                ) : (
                  <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', overflow: 'hidden' }}>
                    {data.priority_contacts.map((ct, i) => {
                      const score = ct.total_score;
                      const scoreColour = score >= 8 ? '#276749' : score >= 6 ? '#B7791F' : '#888';
                      const scoreBg = score >= 8 ? '#F0FFF4' : score >= 6 ? '#FFFBEB' : '#F7F7F7';
                      return (
                        <div key={ct.contact_id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.9rem 1.25rem', borderBottom: i < data.priority_contacts.length - 1 ? '0.5px solid #F0EDE9' : 'none' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem' }}>
                              <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: '500', color: '#1A1A1A' }}>{ct.full_name}</p>
                              <span style={{ fontSize: '0.75rem', fontWeight: '600', color: scoreColour, backgroundColor: scoreBg, padding: '0.2rem 0.5rem', borderRadius: '20px', flexShrink: 0 }}>{score}/10</span>
                            </div>
                            <p style={{ margin: 0, fontSize: '0.75rem', color: '#888' }}>{ct.job_title} · <span style={{ color: '#8B1A1A' }}>{ct.company_name}</span></p>
                          </div>
                          <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                            {ct.enrichment_data?.insight && (
                              <button onClick={() => setSelectedContact(ct)} style={{ fontSize: '0.78rem', color: '#4A5568', background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.35rem 0.75rem', cursor: 'pointer' }}>View</button>
                            )}
                            <button onClick={() => navigate(`/clients/${clientId}/sequences?contactId=${ct.contact_id}&generating=true`)}
                              style={{ fontSize: '0.78rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.35rem 0.85rem', cursor: 'pointer' }}>
                              Sequence →
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Pending approvals */}
              {data.pending_sequences?.length > 0 && (
                <div>
                  <SectionLabel count={data.pending_sequences.length}>Pending approval</SectionLabel>
                  <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderLeft: '3px solid #B7791F', borderRadius: '10px', overflow: 'hidden' }}>
                    {data.pending_sequences.map((seq, i) => (
                      <div key={seq.id} onClick={() => navigate(`/clients/${clientId}/sequences?contactId=${seq.contact_id}`)}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1.25rem', borderBottom: i < data.pending_sequences.length - 1 ? '0.5px solid #F0EDE9' : 'none', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FAFAF8'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <div>
                          <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '500', color: '#1A1A1A' }}>{seq.contact_name}</p>
                          <p style={{ margin: '0.1rem 0 0', fontSize: '0.75rem', color: '#888' }}>{seq.company_name}</p>
                        </div>
                        <span style={{ fontSize: '0.7rem', color: '#B7791F', backgroundColor: '#FFFBEB', padding: '0.2rem 0.6rem', borderRadius: '20px', fontWeight: '500' }}>approve →</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <SectionLabel>Overview</SectionLabel>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
                  {[
                    { label: 'Signals', value: data.today_signals?.length || 0, accent: '#8B1A1A' },
                    { label: 'Pending', value: data.pending_sequences?.length || 0, accent: '#B7791F' },
                    { label: 'Priority', value: data.priority_contacts?.length || 0, accent: '#276749' },
                    { label: 'Sequences', value: (data.pending_sequences?.length || 0), accent: '#1A56A0' },
                  ].map(s => (
                    <div key={s.label} style={{ backgroundColor: s.accent, borderRadius: '10px', padding: '1rem' }}>
                      <p style={{ margin: '0 0 0.25rem', fontSize: '1.6rem', fontWeight: '500', color: '#fff', lineHeight: 1 }}>{s.value}</p>
                      <p style={{ margin: 0, fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <SectionLabel>Recent activity</SectionLabel>
                <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', overflow: 'hidden' }}>
                  {!data.recent_activity?.length ? (
                    <p style={{ padding: '1.5rem', color: '#888', fontSize: '0.85rem', margin: 0, textAlign: 'center' }}>No activity yet.</p>
                  ) : data.recent_activity.map((a, i) => (
                    <div key={a.id} style={{ display: 'flex', gap: '0.75rem', padding: '0.65rem 1rem', borderBottom: i < data.recent_activity.length - 1 ? '0.5px solid #F0EDE9' : 'none', alignItems: 'flex-start' }}>
                      <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#8B1A1A', flexShrink: 0, marginTop: '0.35rem' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: '500', color: '#1A1A1A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.action_type?.replace(/_/g, ' ')}</p>
                          <p style={{ margin: 0, fontSize: '0.7rem', color: '#AAA', whiteSpace: 'nowrap', marginLeft: '0.5rem' }}>{fmt(a.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.25rem' }}>
                <p style={{ margin: '0 0 0.75rem', fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: '500' }}>Quick links</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {[
                    { label: 'My ICP', path: `/clients/${clientId}/icp`, icon: '🎯', desc: 'Define your ideal customer' },
                    { label: 'Discovery brief', path: `/clients/${clientId}/discovery`, icon: '📋', desc: 'Your business context' },
                    { label: 'Prospects', path: `/clients/${clientId}/prospects`, icon: '🏢', desc: 'Companies and contacts' },
                    { label: 'Sequences', path: `/clients/${clientId}/sequences`, icon: '✉️', desc: 'Outreach sequences' },
                    { label: 'Intelligence', path: `/clients/${clientId}/intelligence`, icon: '🔍', desc: 'Market intelligence digests' },
                    { label: 'Activity log', path: `/clients/${clientId}/activity`, icon: '📊', desc: 'Platform activity' },
                    { label: 'Webhooks', path: `/clients/${clientId}/webhooks`, icon: '🔌', desc: 'Real-time event integrations' },
                  ].map(link => (
                    <button key={link.path} onClick={() => navigate(link.path)}
                      style={{ background: 'none', border: '0.5px solid #E8E4E0', borderRadius: '8px', textAlign: 'left', padding: '0.65rem 0.85rem', cursor: 'pointer', width: '100%', display: 'flex', alignItems: 'center', gap: '0.65rem' }}
                      onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#FFF5F5'; e.currentTarget.style.borderColor = '#8B1A1A'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = '#E8E4E0'; }}>
                      <span style={{ fontSize: '1rem', flexShrink: 0 }}>{link.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: '500', color: '#1A1A1A' }}>{link.label}</p>
                        <p style={{ margin: 0, fontSize: '0.7rem', color: '#888' }}>{link.desc}</p>
                      </div>
                      <span style={{ fontSize: '0.75rem', color: '#8B1A1A', flexShrink: 0 }}>→</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {clientId && (
          <div style={{ marginTop: '2rem' }}>
            <SequenceConfigPanel level="client" id={clientId} />
          </div>
        )}
      </div>

      {selectedContact && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: '480px', height: '100vh', backgroundColor: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '1.5rem', borderBottom: '0.5px solid #E8E4E0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: '#1A1A1A' }}>{selectedContact.full_name}</h2>
                  <span style={{ fontSize: '0.75rem', fontWeight: '600', color: selectedContact.total_score >= 8 ? '#276749' : selectedContact.total_score >= 6 ? '#B7791F' : '#888', backgroundColor: selectedContact.total_score >= 8 ? '#F0FFF4' : selectedContact.total_score >= 6 ? '#FFFBEB' : '#F7F7F7', padding: '0.2rem 0.5rem', borderRadius: '20px' }}>{selectedContact.total_score}/10</span>
                </div>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>{selectedContact.job_title} · <span style={{ color: '#8B1A1A' }}>{selectedContact.company_name}</span></p>
              </div>
              <button onClick={() => setSelectedContact(null)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: '#888', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>
              {selectedContact.enrichment_data?.insight ? (
                <div>
                  <p style={{ margin: '0 0 0.5rem', fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: '500' }}>Insight</p>
                  <p style={{ margin: 0, fontSize: '0.88rem', color: '#1A1A1A', lineHeight: '1.6', fontStyle: 'italic' }}>{selectedContact.enrichment_data.insight}</p>
                </div>
              ) : (
                <p style={{ margin: 0, color: '#AAA', fontSize: '0.85rem' }}>No enrichment data available.</p>
              )}
            </div>
            <div style={{ padding: '1.25rem 1.5rem', borderTop: '0.5px solid #E8E4E0', flexShrink: 0 }}>
              <button onClick={() => { setSelectedContact(null); navigate(`/clients/${clientId}/sequences?contactId=${selectedContact.contact_id}&generating=true`); }}
                style={{ width: '100%', backgroundColor: '#8B1A1A', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.65rem', fontSize: '0.875rem', cursor: 'pointer', fontWeight: '500' }}>
                Sequence →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientDashboard;
